import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Users, BarChart3, UserCog, Shield, LogOut, MapPin, Sparkles } from 'lucide-react';
import { useAdminAuth } from '../AuthProvider';

export function AppSidebar({ isOpen = true, onClose }: { isOpen?: boolean; onClose?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();

  const mainItems = [
    { title: 'Dashboard', url: '/admin/dashboard', icon: BarChart3 },
    { title: 'Contacts', url: '/admin/contacts', icon: Users },
  ];

  const adminItems = [
    { title: 'Users', url: '/admin/users', icon: UserCog },
    { title: 'Group Tours', url: '/admin/group-tours', icon: MapPin },
    { title: 'Experiences', url: '/admin/experiences', icon: Sparkles },
    { title: 'Permissions', url: '/admin/permissions', icon: Shield },
  ];

  const auth = useAdminAuth();

  const handleLogout = () => {
    try {
      // call auth provider logout to clear token/localstorage
      auth.logout();
    } catch (e) {
      // fallback to any global hook
      try {
        ; (window as any).adminLogout?.();
      } catch (err) {
        // ignore
      }
    }
    navigate('/admin');
    onClose?.();
  };

  const isActive = (url: string) => {
    try {
      return location.pathname === url;
    } catch (e) {
      return false;
    }
  };

  return (
    <aside
      className={`bg-neutral-900 text-neutral-100 flex flex-col h-screen transition-all duration-200 ${isOpen ? 'w-64' : 'w-16'
        }`}
      aria-hidden={!isOpen}
    >
      <div className="p-4 border-b border-neutral-800 flex items-center gap-3">
        <div className="w-10 h-10 bg-sky-600 rounded-lg flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        {isOpen && (
          <div>
            <h2 className="text-lg font-semibold">CRM Platform</h2>
            <p className="text-xs text-neutral-400">Enterprise Edition</p>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-auto px-2 py-4">
        <div className="mb-4">
          <div className="px-2 text-xs text-neutral-400 uppercase mb-2">Main Menu</div>
          <ul className="space-y-1">
            {mainItems.map((item) => (
              <li key={item.title}>
                <button
                  onClick={() => navigate(item.url)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-neutral-800 text-left ${isActive(item.url) ? 'bg-neutral-800 font-medium' : ''
                    }`}
                  data-testid={`link-${item.title.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4" />
                  {isOpen && <span>{item.title}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="px-2 text-xs text-neutral-400 uppercase mb-2">Administration</div>
          <ul className="space-y-1">
            {adminItems.map((item) => (
              <li key={item.title}>
                <button
                  onClick={() => navigate(item.url)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-neutral-800 text-left ${isActive(item.url) ? 'bg-neutral-800 font-medium' : ''
                    }`}
                  data-testid={`link-${item.title.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4" />
                  {isOpen && <span>{item.title}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="p-4 border-t border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 min-w-0 flex-1 text-left"
              title="Logout"
              data-testid="button-logout-avatar"
            >
              <div className="w-8 h-8 rounded-full bg-neutral-700 flex items-center justify-center text-xs text-white">{
                (auth.user && auth.user.name ? auth.user.name.charAt(0).toUpperCase() : 'U')
              }</div>
              {isOpen && (
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{auth.user?.name ?? 'Admin User'}</p>
                  <p className="text-xs text-neutral-400">{auth.user ? auth.user.email ?? 'Administrator' : 'Administrator'}</p>
                </div>
              )}
            </button>

            {isOpen && (
              <button onClick={handleLogout} className="ml-2 text-neutral-300 hover:text-white p-2 rounded-md" title="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default AppSidebar;
